import React from 'react';
import { IAccommodationProduct, IAccomodationProductOptions, IProduct } from 'services/BackendApi';
import * as Inputs from '../Inputs';
import FluidButton from 'ui/FluidButton';
import { Radio } from 'ui/Radio';
import { produce } from 'immer';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { StyledDateRangeInput } from 'containers/BookingList/StyledFilters';
import { format } from 'date-fns';
import { DecimalInput } from 'ui/stateful/DecimalInput';

export interface ILiveRateMarkupRange {
  from: string;
  to?: string;
  amount: string;
  type: 'flat' | 'percentage' | null;
}

type IRoomWithProductMarkups = Partial<IAccommodationProduct> & {
  productMarkups?: ILiveRateMarkupRange[];
  liveRateMarkups?: ILiveRateMarkupRange[];
};

export interface iRoomProps {
  room: IRoomWithProductMarkups;
  onUpdate: (field: string, value: any) => void;
  onCta: (room: Partial<IProduct<IAccomodationProductOptions>>) => void;
  ctaLabel?: string;
}

const MARKUP_TYPE_OPTIONS: { value: 'flat' | 'percentage' | null; label: string }[] = [
  { value: null, label: 'None' },
  { value: 'flat', label: 'Flat' },
  { value: 'percentage', label: 'Percentage' },
];

export const Room = (props: iRoomProps) => {
  const productMarkups: ILiveRateMarkupRange[] = props.room.productMarkups || props.room.liveRateMarkups || [];
  const updateProductMarkups = (updatedMarkups: ILiveRateMarkupRange[]) => {
    const normalized = updatedMarkups.map(markup => ({
      ...markup,
      to: markup.to === '' ? null : markup.to,
    }));

    props.onUpdate('productMarkups', normalized);
  };

  return (
    <div className="flex flex-col space-y-4 w-full">
      <Inputs.Input
        label={'Name'}
        onChange={val => {
          props.onUpdate('name', val);
        }}
        value={props.room.name || ''}
      />
      <Inputs.Input
        label={'External Product ID'}
        onChange={val => {
          props.onUpdate('externalProductId', val === '' ? null : val);
        }}
        value={props.room.externalProductId || ''}
      />

      <fieldset className="border border-solid border-gray-40 p-[16px] flex flex-col space-y-[16px]">
        <legend className="font-pt-sans text-sm px-[4px]">Live Rates Markup</legend>

        <div className="flex flex-row items-start gap-[24px]">
          <div className="flex flex-col space-y-[4px]">
            <span className="font-pt-sans text-sm">Default Amount</span>
            <DecimalInput
              className="focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 font-pt-sans min-h-37px max-h-37px"
              decimalPlaces={2}
              allowNull={true}
              value={props.room.externalMarkupAmount ?? ''}
              onBlur={val => {
                props.onUpdate('externalMarkupAmount', val === null ? null : String(val));
              }}
            />
          </div>
          <div className="flex flex-col space-y-[4px]">
            <span className="font-pt-sans text-sm">Type</span>
            <div className="flex flex-row items-center gap-[16px] min-h-[37px]">
              {MARKUP_TYPE_OPTIONS.map(opt => (
                <label key={String(opt.value)} className="flex items-center gap-[6px] cursor-pointer font-pt-sans">
                  <Radio
                    checked={props.room.externalMarkupType === opt.value}
                    onClick={() => props.onUpdate('externalMarkupType', opt.value)}
                  />
                  <span className="font-pt-sans">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {productMarkups.map((markup, index) => (
          <div key={index} className="flex flex-row items-end gap-[16px] flex-wrap">
            <div className="flex flex-col space-y-[4px]">
              <span className="font-pt-sans text-sm">From</span>
              <DatePickerStateProvider
                defaultSelectedDates={markup.from ? [markup.from] : []}
                placeholder="SELECT DATE"
                onDateChange={dates => {
                  const date = format(new Date(dates[0]), 'yyyy-MM-dd');
                  const updated = produce(productMarkups, draft => {
                    draft[index].from = date;
                  });
                  updateProductMarkups(updated);
                }}
                isSingleDateSelection
                render={(params: IDatePickerSateParams) => {
                  return (
                    <StyledDateRangeInput
                      displayString={params.displayString}
                      currentDate={params.datePickerCurrentDate}
                      totalNights={params.totalNights}
                      selectedDates={params.selectedDates}
                      onDayClick={params.handleDayClick}
                      onDayMouseOver={params.handleDateMouseOver}
                      showDatePicker={params.showDatePicker}
                      onNextClick={params.incrementDate}
                      onPrevClick={params.decrementDate}
                      onMouseDown={params.toggleDatePicker}
                      onClickOutside={params.hideDatePicker}
                      calendarCount={1}
                      customWidth={1}
                      noPortal
                      showTotalNights={false}
                    />
                  );
                }}
              />
            </div>
            <div className="flex flex-col space-y-[4px]">
              <span className="font-pt-sans text-sm">To</span>
              <DatePickerStateProvider
                defaultSelectedDates={markup.to ? [markup.to] : []}
                placeholder="SELECT DATE"
                onDateChange={dates => {
                  const date = format(new Date(dates[0]), 'yyyy-MM-dd');
                  const updated = produce(productMarkups, draft => {
                    draft[index].to = date;
                  });
                  updateProductMarkups(updated);
                }}
                isSingleDateSelection
                render={(params: IDatePickerSateParams) => {
                  return (
                    <StyledDateRangeInput
                      displayString={params.displayString}
                      currentDate={params.datePickerCurrentDate}
                      totalNights={params.totalNights}
                      selectedDates={params.selectedDates}
                      onDayClick={params.handleDayClick}
                      onDayMouseOver={params.handleDateMouseOver}
                      showDatePicker={params.showDatePicker}
                      onNextClick={params.incrementDate}
                      onPrevClick={params.decrementDate}
                      onMouseDown={params.toggleDatePicker}
                      onClickOutside={params.hideDatePicker}
                      calendarCount={1}
                      customWidth={1}
                      noPortal
                      showTotalNights={false}
                    />
                  );
                }}
              />
            </div>
            <div className="flex flex-col space-y-[4px]">
              <span className="font-pt-sans text-sm">Markup Amount</span>
              <DecimalInput
                className="focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 font-pt-sans min-h-37px max-h-37px"
                decimalPlaces={2}
                allowNull={true}
                value={markup.amount || ''}
                onBlur={val => {
                  const updated = produce(productMarkups, draft => {
                    draft[index].amount = val === null ? '' : String(val);
                  });
                  updateProductMarkups(updated);
                }}
              />
            </div>
            <div className="flex flex-col space-y-[4px]">
              <span className="font-pt-sans text-sm">Markup Type</span>
              <div className="flex flex-row items-center gap-[16px] min-h-[37px]">
                {MARKUP_TYPE_OPTIONS.slice(1).map(opt => (
                  <label key={String(opt.value)} className="flex items-center gap-[6px] cursor-pointer font-pt-sans">
                    <Radio
                      checked={markup.type === opt.value}
                      onClick={() => {
                        const updated = produce(productMarkups, draft => {
                          draft[index].type = opt.value;
                        });
                        updateProductMarkups(updated);
                      }}
                    />
                    <span className="font-pt-sans">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              className="rounded-full w-[24px] h-[24px] bg-brown-60 hover:bg-brown-80 text-white text-xs cursor-pointer flex-shrink-0 mb-[8px]"
              onClick={() => {
                const updated = productMarkups.filter((_, i) => i !== index);
                updateProductMarkups(updated);
              }}
            >
              &#x2715;
            </button>
          </div>
        ))}

        <FluidButton
          type="secondary"
          className="w-[220px] self-start"
          onClick={() => {
            const updated: ILiveRateMarkupRange[] = [
              ...productMarkups,
              { from: '', amount: '', type: 'flat' },
            ];
            updateProductMarkups(updated);
          }}
        >
          Add Date Range Markup
        </FluidButton>
      </fieldset>

      <Inputs.Input
        label={'Category Type'}
        onChange={val => {
          props.onUpdate('meta', {
            ...props.room.meta,
            categoryType: val,
          });
        }}
        value={props.room.meta?.categoryType || ''}
      />
      <Inputs.Checkbox
        label={'Room Always charges Standard Occupancy quantity of adult Meal Plans as minimum'}
        onChange={val => {
          props.onUpdate('options', {
            ...props.room.options,
            mealPlanToUseStandardOccupancyMinimum: val,
          });
        }}
        value={props.room.options?.mealPlanToUseStandardOccupancyMinimum || false}
      />
      <Inputs.TextArea
        label={'Description'}
        onChange={val => {
          props.onUpdate('meta.description', val);
        }}
        value={props.room.meta?.description || ''}
        rows={5}
      />

      <Inputs.RichTextEditor
        label="Room Benefits"
        value={props.room.meta?.moreInformation || ''}
        onChange={val => {
          props.onUpdate('meta.moreInformation', val);
        }}
      />

      <Inputs.Input
        label={'Size'}
        onChange={val => {
          props.onUpdate('meta.size', val === '' ? undefined : parseInt(val));
        }}
        value={props.room.meta?.size?.toString() || ''}
      />

      <Inputs.CrudList
        label={'Amenities'}
        collection={props.room.meta?.amenities || []}
        className="w-full border border-solid border-gray-40 p-2"
        onChange={newList => {
          props.onUpdate('meta.amenities', newList);
        }}
      />

      <Inputs.AgeGroups
        label={'Age Groups'}
        collection={props.room.options?.ages || []}
        onChange={newList => {
          props.onUpdate('options.ages', newList);
        }}
      />

      <Inputs.Input
        label={'Standard Occupancy'}
        onChange={val => {
          props.onUpdate('options.occupancy.standardOccupancy', val === '' ? undefined : parseInt(val));
        }}
        value={props.room.options?.occupancy?.standardOccupancy?.toString() || ''}
      />
      <Inputs.Input
        label={'Maximum People'}
        onChange={val => {
          props.onUpdate('options.occupancy.maximumPeople', val === '' ? undefined : parseInt(val));
        }}
        value={props.room.options?.occupancy?.maximumPeople?.toString() || ''}
      />

      <Inputs.OccupancyLimits
        label={'Occupancy Limits'}
        collection={props.room.options?.occupancy.limits || []}
        onChange={newList => {
          props.onUpdate('options.occupancy.limits', newList);
        }}
      />

      <Inputs.Input
        label="Vimeo Video ID"
        value={props.room.vimeoVideoId || ''}
        onChange={val => {
          props.onUpdate('vimeoVideoId', val);
        }}
      />

      <FluidButton
        type="primary"
        className="self-end w-[200px] mt-4"
        onClick={() => {
          props.onCta(props.room);
        }}
      >
        {props.ctaLabel || 'Update Room'}
      </FluidButton>
    </div>
  );
};
